import React from 'react';
import { Formik } from 'formik';
import { object } from 'yup';
import { Form, FormItem, Input } from 'formik-antd';
import { Button, Form as AntForm } from 'antd';
import { FormikOnSubmit } from 'lib/formik.utils';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { v4 } from 'uuid';
import {
  partnerNameValidationSchema,
  partnerUniqueNumberValidationSchema,
} from 'common/config/validationSchema';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: { offset: 8, span: 14 },
};

export interface BusinessPartnerFormModel {
  name: string;
  uniqueNumber: string;
}

export interface BusinessPartnerFormProps {
  onSubmit: FormikOnSubmit<BusinessPartnerFormModel>;
  loading?: boolean;
  value?: BusinessPartnerFormModel;
  readonly?: boolean;
  isSubpartnerForm?: boolean;
  bpContextID?: number;
}

export const BusinessPartnerForm = ({
  onSubmit,
  loading = false,
  value,
  readonly = false,
  isSubpartnerForm = false,
  bpContextID,
}: BusinessPartnerFormProps) => {
  const intlMessage = useFormatMessage();

  const generetedUUID = bpContextID ? `${bpContextID}-${v4()}` : v4();

  const initialValues: BusinessPartnerFormModel = {
    name: '',
    uniqueNumber: !isSubpartnerForm ? '' : generetedUUID,
  };

  const validationSchema = object().shape({
    name: partnerNameValidationSchema(intlMessage),
    uniqueNumber: partnerUniqueNumberValidationSchema(intlMessage),
    description: partnerUniqueNumberValidationSchema(intlMessage),
  });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={value ?? initialValues}
      onSubmit={onSubmit}
    >
      <Form {...layout} style={{ margin: '50px 0 50px 0' }}>
        <FormItem
          label={intlMessage({ id: 'name' })}
          name="name"
          required={!readonly}
        >
          <Input name="name" readOnly={readonly} />
        </FormItem>

        {!isSubpartnerForm && (
          <FormItem
            label={intlMessage({ id: 'identifier' })}
            name="uniqueNumber"
          >
            <Input name="uniqueNumber" readOnly={readonly} />
          </FormItem>
        )}

        {isSubpartnerForm && (
          <FormItem
            label="UUID"
            name="uniqueNumber"
            required={false}
            //'display:none' (just for sending UUID to backend)
            style={{ display: 'none' }}
          >
            <Input name="uniqueNumber" readOnly={readonly} />
          </FormItem>
        )}

        {isSubpartnerForm && (
          <FormItem
            label={intlMessage({ id: 'identifier' })}
            name="description"
            required={false}
          >
            <Input name="description" />
          </FormItem>
        )}

        {!readonly && (
          <AntForm.Item {...tailLayout}>
            <Button loading={loading} type="primary" htmlType="submit">
              {loading
                ? intlMessage({ id: 'saving' })
                : intlMessage({ id: 'save' })}
            </Button>
          </AntForm.Item>
        )}
      </Form>
    </Formik>
  );
};
